// LARA style loudness radar, drawn on a canvas.
// The radar shows the short-term loudness history in polar form: the angle is time (one revolution
// per RADAR_SLOTS blocks of 100 ms, clockwise, the arm is the newest value) and the radius is the level.
// The segmented ring around it shows the momentary loudness. Levels are in LU relative to the target.

export const RADAR_SLOTS = 600;

// Radar radius: edge of the center hole .. outer edge
const RADAR_MIN = -30;
const RADAR_MAX = 9;
// Momentary ring: bottom .. right side, 5 degrees per LU, 0 LU at the top
const RING_MIN = -36;
const RING_MAX = 18;
const DEG_PER_LU = 5;
const GRID_LEVELS = [-24, -18, -12, -6, 0, 6];
const LABELS = [-30, -24, -18, -12, -6, 0, 6, 12];

// Filled from the outside in: each band covers levels below `to`
const RADAR_BANDS = [
	{to: RADAR_MAX, color: "#f0c030"},
	{to: 0, color: "#66dd6e"},
	{to: -18, color: "#56c8d8"}
];

function ring_color (lu) {
	if (lu < -18) return "#3f7fe6";
	if (lu < 0) return "#66dd6e";
	if (lu < 9) return "#f0c030";
	return "#e5533d";
}

// Canvas angle (radians) for degrees clockwise from the top
function angle (deg) {
	return (deg - 90) * Math.PI / 180;
}

// history: short-term LUFS per slot (NaN = no data), pos: next slot to write (the arm),
// momentary: momentary LUFS, target: target LUFS,
// reserve: {width, height} (CSS px) of an area in the top right corner used by other overlays;
// the radar moves left just enough to clear it
export function drawLufsRadar (canvas, history, pos, momentary, target, reserve) {
	var dpr = window.devicePixelRatio || 1;
	var w = canvas.clientWidth;
	var h = canvas.clientHeight;
	if (!w || !h) return;
	var cw = Math.round(w * dpr);
	var ch = Math.round(h * dpr);
	if (canvas.width !== cw || canvas.height !== ch) {
		canvas.width = cw;
		canvas.height = ch;
	}
	var ctx = canvas.getContext("2d");
	ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
	ctx.clearRect(0, 0, w, h);

	var cy = h / 2;
	// Leaves some room at the edges (top and bottom on a 16:9 player)
	var outer = Math.min(w, h) / 2 * 0.88;
	// Centered unless the circle would go under the reserved corner area, but keeps a gap to the left edge.
	// The circle clears the area when its center is far enough left of the area's left edge, given how far
	// below the area the center is.
	var cx = w / 2;
	if (reserve && reserve.width && reserve.height) {
		var dy = Math.max(0, cy - reserve.height);
		var dx = dy < outer ? Math.sqrt(outer * outer - dy * dy) : 0;
		cx = Math.min(cx, w - reserve.width - dx);
	}
	cx = Math.max(outer + 6, cx);
	var font = Math.max(8, outer * 0.085);
	var label_r = outer - font * 0.7;
	var ring_outer = label_r - font * 0.9;
	var ring_inner = ring_outer - Math.max(3, outer * 0.06);
	var radar_r = ring_inner - Math.max(2, outer * 0.04);
	var hole_r = radar_r * 0.22;
	var radius = lu => hole_r + (Math.min(RADAR_MAX, Math.max(RADAR_MIN, lu)) - RADAR_MIN) / (RADAR_MAX - RADAR_MIN) * (radar_r - hole_r);

	// Radar: union of a wedge per slot, filled with the level bands
	var shape = new Path2D();
	var step = 360 / RADAR_SLOTS;
	for (var i = 0; i < RADAR_SLOTS; i++) {
		var v = history[i] - target;
		if (!isFinite(v) || v <= RADAR_MIN) continue;
		var a0 = angle(i * step);
		var a1 = angle((i + 1) * step) + 0.002;
		shape.moveTo(cx, cy);
		shape.arc(cx, cy, radius(v), a0, a1);
		shape.closePath();
	}
	ctx.save();
	ctx.clip(shape);
	RADAR_BANDS.forEach(b => {
		ctx.beginPath();
		ctx.arc(cx, cy, radius(b.to), 0, Math.PI * 2);
		ctx.fillStyle = b.color;
		ctx.fill();
	});
	ctx.restore();

	// The oldest values just ahead of the arm fade out
	var arm = pos * step;
	if (ctx.createConicGradient) {
		var fade = ctx.createConicGradient(angle(arm), cx, cy);
		fade.addColorStop(0, "rgba(0, 0, 0, 1)");
		fade.addColorStop(40 / 360, "rgba(0, 0, 0, 0)");
		fade.addColorStop(1, "rgba(0, 0, 0, 0)");
		ctx.save();
		ctx.globalCompositeOperation = "destination-out";
		ctx.beginPath();
		ctx.arc(cx, cy, radar_r + 1, 0, Math.PI * 2);
		ctx.fillStyle = fade;
		ctx.fill();
		ctx.restore();
	}

	// Dark backdrop (also under the labels) so the radar and the text stay readable on bright video.
	// Drawn behind the radar after the fade, so the fade doesn't cut it.
	ctx.save();
	ctx.globalCompositeOperation = "destination-over";
	ctx.beginPath();
	ctx.arc(cx, cy, outer, 0, Math.PI * 2);
	ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
	ctx.fill();
	ctx.restore();

	// Grid: level rings and spokes every 30 degrees
	ctx.lineWidth = 1;
	ctx.strokeStyle = "rgba(20, 60, 100, 0.9)";
	GRID_LEVELS.forEach(lu => {
		ctx.beginPath();
		ctx.arc(cx, cy, radius(lu), 0, Math.PI * 2);
		ctx.stroke();
	});
	ctx.beginPath();
	for (var d = 0; d < 360; d += 30) {
		ctx.moveTo(cx + Math.cos(angle(d)) * hole_r, cy + Math.sin(angle(d)) * hole_r);
		ctx.lineTo(cx + Math.cos(angle(d)) * radar_r, cy + Math.sin(angle(d)) * radar_r);
	}
	ctx.stroke();

	// Arm
	ctx.beginPath();
	ctx.moveTo(cx + Math.cos(angle(arm)) * hole_r, cy + Math.sin(angle(arm)) * hole_r);
	ctx.lineTo(cx + Math.cos(angle(arm)) * radar_r, cy + Math.sin(angle(arm)) * radar_r);
	ctx.strokeStyle = "rgba(170, 255, 170, 0.7)";
	ctx.lineWidth = Math.max(1, outer * 0.008);
	ctx.stroke();

	// Center hole
	ctx.beginPath();
	ctx.arc(cx, cy, hole_r, 0, Math.PI * 2);
	ctx.fillStyle = "#000";
	ctx.fill();

	// Momentary ring: segment k covers k .. k+1 LU and is lit when the level is above k
	var m = momentary - target;
	var half = (DEG_PER_LU - 1.5) / 2;
	ctx.lineWidth = ring_outer - ring_inner;
	for (var k = RING_MIN; k < RING_MAX; k++) {
		var deg = (k + 0.5) * DEG_PER_LU;
		ctx.beginPath();
		ctx.arc(cx, cy, (ring_inner + ring_outer) / 2, angle(deg - half), angle(deg + half));
		ctx.strokeStyle = isFinite(m) && m > k ? ring_color(k) : "rgba(120, 120, 120, 0.55)";
		ctx.stroke();
	}

	// Scale labels with a dark outline
	ctx.font = Math.round(font) + "px sans-serif";
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";
	ctx.lineJoin = "round";
	ctx.lineWidth = Math.max(2, font * 0.25);
	ctx.strokeStyle = "rgba(0, 0, 0, 0.8)";
	ctx.fillStyle = "#fff";
	LABELS.forEach(lu => {
		var a = angle(lu * DEG_PER_LU);
		var text = (lu > 0 ? "+" : "") + lu;
		var x = cx + Math.cos(a) * label_r;
		var y = cy + Math.sin(a) * label_r;
		ctx.strokeText(text, x, y);
		ctx.fillText(text, x, y);
	});
}
